import React from "react";
import { BiPlus } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { FieldCard } from "./FieldCard";
import { UnlockBadge } from "./UnlockBadge";
import { SpaceEmptyState } from "./SpaceEmptyState";

interface FieldsGridProps {
  fields: Record<string, unknown>;
  onEditField: (fieldKey: string) => void;
  onDeleteField?: (fieldKey: string) => void;
  onCreateField: () => void;
  maxLimit: number;
  isLargeBusinessTier: boolean;
  isReadOnlyFolder?: boolean;
  readOnlyTooltip?: string;
  onUpgradeClick?: () => void;
}

export const FieldsGrid: React.FC<FieldsGridProps> = ({
  fields,
  onEditField,
  onDeleteField,
  onCreateField,
  maxLimit,
  isLargeBusinessTier,
  isReadOnlyFolder = false,
  readOnlyTooltip,
  onUpgradeClick,
}) => {
  const { t } = useTranslation();
  const fieldCount = Object.keys(fields).length;
  const canAddMore = fieldCount < maxLimit;

  if (fieldCount === 0) {
    return (
      <SpaceEmptyState
        title={t("common.noFieldsYet")}
        subtitle={t("common.createFirstField")}
        actionLabel={
          isReadOnlyFolder ? undefined : (t("common.addField") as string)
        }
        onAction={isReadOnlyFolder ? undefined : onCreateField}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(fields).map(([fieldKey, fieldValue]) => (
        <FieldCard
          key={fieldKey}
          fieldKey={fieldKey}
          fieldValue={fieldValue}
          onEdit={onEditField}
          onDelete={isReadOnlyFolder ? undefined : onDeleteField}
          isReadOnly={isReadOnlyFolder}
          readOnlyTooltip={readOnlyTooltip}
        />
      ))}

      {canAddMore && !isReadOnlyFolder && (
        <button
          onClick={onCreateField}
          className="group flex min-h-[128px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white p-4 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition-colors group-hover:border-blue-400 group-hover:text-blue-600">
            <BiPlus size={20} />
          </span>
          <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
            {isLargeBusinessTier
              ? t("common.createField")
              : t("common.fieldsCount", {
                  count: fieldCount,
                  max: maxLimit,
                })}
          </span>
        </button>
      )}

      {!canAddMore &&
        !isLargeBusinessTier &&
        !isReadOnlyFolder &&
        onUpgradeClick && (
          <button
            onClick={onUpgradeClick}
            className="group flex min-h-[128px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50 p-4 text-center transition-colors hover:border-blue-500"
          >
            <UnlockBadge />
            <span className="text-sm font-semibold text-blue-700">
              {t("common.upgradeForMore")}
            </span>
            <span className="text-xs text-gray-600">
              {t("common.getMoreFields", { limit: 20 })}
            </span>
          </button>
        )}
    </div>
  );
};

export default FieldsGrid;
